// Compass design system MUI theme
// Uses CSS custom properties (var(--cx-*)) in component overrides for automatic sync with cx.css
// Uses hex values in MUI core palette (MUI needs concrete values for internal color calculations)

'use client'
import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import { Sizes } from '@/lib/constants/sizes'
import { typescale, typography, typographyVariantMapping } from './typography'
import { compassPalette } from './colors'

const theme = createTheme({
	breakpoints: { values: { xs: 0, sm: 360, md: 768, lg: 1280, xl: 1536 } },
	spacing: [0, 4, 8, 16, 24, 32, 40, 48, 64],
	shape: { borderRadius: 2 },
	palette: {
		...compassPalette,
		stroke: {
			...compassPalette.stroke,
			brand: {
				...compassPalette.stroke.brand,
				otherRevenue: compassPalette.stroke.brand.primary,
			},
		},
	},
	typography: {
		...typography,
	},
	components: {
		MuiTypography: {
			defaultProps: {
				variantMapping: typographyVariantMapping,
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					backgroundColor: 'var(--cx-color-grey40)',
					borderRadius: '4px',
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					'textTransform': 'none',
					'color': 'var(--cx-color-text)',
					'borderRadius': '4px !important',
					'fontSize': typescale.extraSmall.fontSize,
					'lineHeight': typescale.extraSmall.lineHeight,
					'fontWeight': 500,
					'borderColor': 'var(--cx-color-grey40)',
					'&.Mui-selected': {
						backgroundColor: 'var(--cx-color-white)',
						borderColor: 'var(--cx-color-grey40)',
					},
					'&.Mui-selected:hover': {
						backgroundColor: 'var(--cx-color-white)',
						borderColor: 'var(--cx-color-grey40)',
					},
					'&:hover': {
						backgroundColor: 'var(--cx-color-white)',
						borderColor: 'var(--cx-color-grey40)',
					},
					'@media (hover: none)': {
						'&:hover': {
							backgroundColor: 'inherit',
							borderColor: 'var(--cx-color-grey40)',
						},
					},
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				colorPrimary: {
					backgroundColor: 'var(--cx-color-white)',
					color: 'var(--cx-color-text)',
				},
			},
		},
		MuiAvatar: {
			styleOverrides: {
				circular: {
					backgroundColor: 'var(--cx-color-grey70)',
					fontWeight: 500,
					color: 'var(--cx-color-text)',
				},
			},
		},
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true,
			},
			styleOverrides: {
				root: {
					'&.MuiButtonBase-root': {
						'&.core-ui-input': {
							'&.Mui-selected': {
								'backgroundColor': compassPalette.surface.brand.primaryHighlight,
								'&:hover': {
									backgroundColor: compassPalette.surface.brand.primaryHighlight,
								},
							},
							'&.Mui-focusVisible': {
								outline: '2px solid var(--cx-color-black)',
								outlineOffset: '-4px',
							},
							'&.Mui-disabled': {
								opacity: 0.38,
							},
							'&.Mui-readOnly': {
								'backgroundColor': compassPalette.interaction.disabledOpacity,
								'borderColor': 'var(--cx-color-border)',
								'&.Mui-focused': {
									borderColor: 'var(--cx-color-border)',
									borderWidth: '1px',
								},
								'&:hover': {
									backgroundColor: compassPalette.interaction.disabledOpacity,
								},
								'&:active': {
									backgroundColor: compassPalette.interaction.disabledOpacity,
								},
							},
						},
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					'&.multi-select-textfield': {
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: 'var(--cx-color-black)',
							borderWidth: '2px',
						},
						'&.Mui-focused.open .MuiOutlinedInput-notchedOutline': {
							borderColor: 'var(--cx-color-interactive)',
							borderWidth: '1px',
						},
						'&:before': {
							border: 'none',
						},
						'&:hover:not(.Mui-disabled, .Mui-error):before': {
							border: 'none',
						},
						'&:focus .MuiSelect-nativeInput': {
							backgroundColor: 'transparent',
						},
						'&:hover': {
							'backgroundColor': compassPalette.interaction.neutralHoverOpacity,
							'border': 'none',
							'.MuiOutlinedInput-notchedOutline': {
								borderColor: 'var(--cx-color-border)',
								borderWidth: '1px',
							},
						},
						'@media (hover: none)': {
							'&:hover': {
								color: compassPalette.secondary.main,
								backgroundColor: 'transparent',
							},
						},
						'&:active': {
							backgroundColor: compassPalette.interaction.neutralPressOpacity,
						},
						'@media (max-width: 400px)': {
							whiteSpace: 'unset !important',
							overflow: 'unset !important',
						},
						'& .MuiInputBase-input': {
							color: 'var(--cx-color-text)',
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled': {
							color: `${compassPalette.secondary.main}`,
							backgroundColor: `${compassPalette.interaction.neutralHoverOpacity}`,
						},
						'&.Mui-disabled:before': {
							border: 'none !important',
						},
						'&.Mui-disabled:after': {
							border: 'none !important',
						},
						'& .MuiInputBase-root.Mui-disabled:before': {
							border: 'none ',
						},
						'& .MuiInputBase-root.Mui-disabled:after': {
							border: 'none',
						},
						'&:after': {
							border: 'none',
						},
						'.MuiOutlinedInput-notchedOutline': {
							borderColor: 'var(--cx-color-border)',
						},
					},
				},
			},
		},
		MuiList: {
			styleOverrides: {
				root: {
					padding: 0,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					borderRadius: '4px',
				},
			},
			variants: [
				{
					props: {
						variant: 'bespoke',
						color: 'primary',
					},
					style: {
						'color': 'var(--cx-color-white)',
						'border': '1px solid',
						'borderColor': 'var(--cx-color-interactive)',
						'background': 'linear-gradient(var(--cx-color-interactive), var(--cx-color-interactive))',
						'&:hover': {
							color: 'var(--cx-color-white)',
							border: '1px solid',
							borderColor: compassPalette.foreground.brand.primaryDepth,
							background: `linear-gradient(${compassPalette.interaction.brandHoverOpacity}, ${compassPalette.interaction.brandHoverOpacity}), linear-gradient(var(--cx-color-interactive), var(--cx-color-interactive))`,
						},
						'@media (hover: none)': {
							'&:hover': {
								color: 'var(--cx-color-white)',
								border: '1px solid',
								borderColor: 'var(--cx-color-interactive)',
								background: 'linear-gradient(var(--cx-color-interactive), var(--cx-color-interactive))',
							},
						},
						'&:active': {
							color: 'var(--cx-color-white)',
							border: '1px solid',
							borderColor: compassPalette.foreground.brand.primaryDepth,
							background: `linear-gradient(${compassPalette.interaction.brandPressOpacity}, ${compassPalette.interaction.brandPressOpacity}), linear-gradient(var(--cx-color-interactive), var(--cx-color-interactive))`,
						},
						'&:focus-visible': {
							color: 'var(--cx-color-white)',
							border: '1px solid',
							borderColor: 'var(--cx-color-interactive)',
							backgroundColor: 'var(--cx-color-interactive)',
						},
						'&.Mui-disabled': {
							color: 'var(--cx-color-textDisabled)',
							borderColor: compassPalette.interaction.disabledOpacity,
							background: `linear-gradient(${compassPalette.interaction.disabledOpacity}, ${compassPalette.interaction.disabledOpacity})`,
						},
						'& .MuiButton-startIcon': {
							color: 'var(--cx-color-white)',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						color: 'secondary',
					},
					style: {
						'color': 'var(--cx-color-textSubtle)',
						'border': '1px solid',
						'borderColor': 'var(--cx-color-border)',
						'backgroundColor': 'var(--cx-color-white)',
						'&:hover': {
							borderColor: 'var(--cx-color-border)',
							backgroundColor: compassPalette.interaction.neutralHoverOpacity,
						},
						'@media (hover: none)': {
							'&:hover': {
								color: 'var(--cx-color-textSubtle)',
								border: '1px solid',
								borderColor: 'var(--cx-color-border)',
								backgroundColor: 'var(--cx-color-white)',
							},
						},
						'&:active': {
							borderColor: 'var(--cx-color-border)',
							backgroundColor: compassPalette.interaction.neutralPressOpacity,
						},
						'&:focus-visible': {
							borderColor: 'var(--cx-color-black)',
							outline: '1px solid var(--cx-color-black)',
							backgroundColor: 'var(--cx-color-white)',
						},
						'&.Mui-disabled': {
							backgroundColor: compassPalette.interaction.disabledOpacity,
							color: 'var(--cx-color-textDisabled)',
						},
						'& .MuiButton-startIcon': {
							color: 'var(--cx-color-textSubtle)',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						color: 'tertiary',
					},
					style: {
						'color': 'var(--cx-color-textSubtle)',
						'border': '1px solid',
						'borderColor': 'transparent',
						'backgroundColor': 'transparent',
						'&:hover': {
							borderColor: compassPalette.interaction.neutralHoverOpacity,
							backgroundColor: compassPalette.interaction.neutralHoverOpacity,
						},
						'@media (hover: none)': {
							'&:hover': {
								color: 'var(--cx-color-textSubtle)',
								border: '1px solid',
								borderColor: 'transparent',
								backgroundColor: 'transparent',
							},
						},
						'&:active': {
							borderColor: compassPalette.interaction.neutralPressOpacity,
							backgroundColor: compassPalette.interaction.neutralPressOpacity,
						},
						'&:focus-visible': {
							borderColor: 'var(--cx-color-black)',
							outline: '1px solid var(--cx-color-black)',
							backgroundColor: 'transparent',
						},
						'&.Mui-disabled': {
							backgroundColor: compassPalette.interaction.disabledOpacity,
							color: 'var(--cx-color-textDisabled)',
						},
						'& .MuiButton-startIcon': {
							color: 'var(--cx-color-textSubtle)',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						size: 'small',
					},
					style: {
						'height': '32px',
						'lineHeight': '32px',
						'fontSize': typescale.small.fontSize,
						'fontWeight': 500,
						'& .MuiButton-startIcon': {
							marginRight: '6px',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						size: 'medium',
					},
					style: {
						'height': '40px',
						'lineHeight': '40px',
						'fontSize': typescale.base.fontSize,
						'fontWeight': 500,
						'& .MuiButton-startIcon': {
							marginRight: '10px',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						size: 'large',
					},
					style: {
						'height': '48px',
						'lineHeight': '48px',
						'fontSize': typescale.mediumMobile.fontSize,
						'fontWeight': 500,
						'& .MuiButton-startIcon': {
							marginRight: '12px',
						},
					},
				},
				{
					props: {
						variant: 'nav',
					},
					style: {
						'paddingTop': 3,
						'color': 'rgba(0, 0, 0, 0.16)',
						'backgroundColor': 'transparent',
						'borderRadius': 0,
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							width: 4,
							height: '100%',
							backgroundColor: 'transparent',
							clipPath: 'polygon(0 0, 100% 5%, 100% 95%, 0 100%)',
							transition: 'background-color 0.3s ease',
						},
						'&:hover::before': {
							backgroundColor: 'var(--cx-color-interactive)',
						},
						'&:hover': {
							backgroundColor: 'transparent',
						},
					},
				},
				{
					props: {
						variant: 'nav',
						size: 'expanded',
					},
					style: {
						height: '50px',
						flexDirection: 'row',
						justifyContent: 'flex-start',
					},
				},
				{
					props: {
						variant: 'nav',
						size: 'collapsed',
					},
					style: {
						height: '72px',
						flexDirection: 'column',
						justifyContent: 'flex-middle',
					},
				},
				{
					props: {
						variant: 'nav',
						color: 'selected',
					},
					style: {
						'color': 'var(--cx-color-interactive)',
						'&::before': {
							backgroundColor: 'var(--cx-color-interactive)',
						},
					},
				},
				{
					props: {
						variant: 'nav',
						color: 'default',
					},
					style: {
						'color': 'rgba(0, 0, 0, 0.54)',
						'&::before': {
							backgroundColor: 'transparent',
						},
					},
				},
			],
		},
		MuiAlert: {
			variants: [
				{
					props: {
						variant: 'outlined',
						severity: 'info',
						className: 'bannerNotification',
					},
					style: {
						'border': '2px solid var(--cx-color-interactive)',
						'padding': 0,
						'overflow': 'hidden',
						'backgroundColor': 'var(--cx-color-white)',
						'& .MuiAlert-icon': {
							backgroundColor: 'var(--cx-color-interactive)',
							padding: '16px 12px',
						},
						'& .MuiSvgIcon-root.recruiting': {
							color: 'var(--cx-color-white)',
							width: 24,
							height: 24,
						},
						'& .MuiSvgIcon-root.bannerIcon-info': {
							color: 'var(--cx-color-white)',
						},
						'& .MuiAlert-message': {
							width: '100%',
						},
					},
				},
				{
					props: {
						variant: 'outlined',
						severity: 'warning',
						className: 'bannerNotification',
					},
					style: {
						'border': '2px solid var(--cx-color-highlight)',
						'padding': 0,
						'overflow': 'hidden',
						'backgroundColor': 'var(--cx-color-white)',
						'& .MuiAlert-icon': {
							backgroundColor: 'var(--cx-color-highlight)',
							borderRadius: '0',
							padding: '16px 12px',
							marginRight: '12px',
						},
						'& .MuiAlert-icon .MuiSvgIcon-root': {
							color: 'var(--cx-color-text)',
							width: 24,
							height: 24,
						},
						'& .MuiAlert-message': {
							width: '100%',
							padding: '8px 0 12px',
						},
					},
				},
				{
					props: {
						variant: 'outlined',
						severity: 'error',
						className: 'bannerNotification',
					},
					style: {
						'border': '2px solid var(--cx-color-critical)',
						'padding': 0,
						'overflow': 'hidden',
						'backgroundColor': 'var(--cx-color-white)',
						'& .MuiAlert-icon': {
							backgroundColor: 'var(--cx-color-critical)',
							borderRadius: '0',
							padding: '16px 12px',
							marginRight: '12px',
						},
						'& .MuiAlert-icon .MuiSvgIcon-root': {
							color: 'var(--cx-color-white)',
							width: 24,
							height: 24,
						},
						'& .MuiAlert-message': {
							width: '100%',
							padding: '8px 0 12px',
						},
					},
				},
			],
		},
		MuiCard: {
			styleOverrides: {
				root: {
					'borderRadius': '.5em',
					'overflowY': 'auto',
					'.bi-listing-metrics-grid &': {
						'height': '100%',
						'display': 'flex',
						'padding': '12px',
						'flexDirection': 'column',
						'& > div': {
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
						},
						'& .MuiStack-root': {
							'height': '100%',
							'justifyContent': 'space-between',
							'& > :first-child': {
								marginBottom: 'auto',
							},
						},
					},
					'.overview-grid &': {
						'height': '100%',
						'display': 'flex',
						'padding': '12px',
						'flexDirection': 'column',
						'& > div': {
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
						},
						'& .MuiStack-root': {
							'height': '100%',
							'justifyContent': 'space-between',
							'& > :first-child': {
								'marginBottom': 'auto',
								'& .MuiBox-root': {
									'& > :first-child': {
										'@media (max-width: 600px)': {
											alignItems: 'baseline',
											minHeight: '36px',
										},
									},
								},
							},
						},
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					fontSize: typescale.extraSmall.fontSize,
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					backgroundColor: 'var(--cx-color-grey10)',
				},
			},
		},
		MuiLink: {
			defaultProps: {
				underline: 'hover',
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				input: {
					padding: '4.5px 4px',
				},
				root: {
					padding: '0 10px',
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'&.core-ui-input': {
						'& .MuiOutlinedInput-root': {
							'&.Mui-disabled': {
								'backgroundColor': compassPalette.interaction.disabledOpacity,
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-border)',
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-border)',
									borderWidth: '1px',
								},
							},
							'&.Mui-readOnly': {
								'backgroundColor': compassPalette.interaction.disabledOpacity,
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-border)',
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-border)',
									borderWidth: '1px',
								},
								'&:hover': {
									backgroundColor: compassPalette.interaction.disabledOpacity,
								},
								'&:active': {
									backgroundColor: compassPalette.interaction.disabledOpacity,
								},
							},
							'backgroundColor': 'transparent',
							'& .MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
							},
							'&:hover': {
								'@media (max-width: 600px)': {
									backgroundColor: 'transparent',
								},
								'&:not(.Mui-focused)': {
									backgroundColor: compassPalette.interaction.neutralHoverOpacity,
								},
							},
							'&:active': {
								'@media (max-width: 600px)': {
									backgroundColor: 'transparent',
								},
								'backgroundColor': compassPalette.interaction.neutralPressOpacity,
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: 'var(--cx-color-interactive)',
								borderWidth: '2px',
								backgroundColor: 'transparent',
							},
						},
					},
				},
			},
			variants: [
				{
					props: { color: 'primary' },
					style: {
						'&.core-ui-input': {
							'& .MuiOutlinedInput-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-border)',
								},
							},
						},
					},
				},
				{
					props: { color: 'error' },
					style: {
						'&.core-ui-input': {
							'& .MuiOutlinedInput-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-critical)',
								},
							},
						},
					},
				},
			],
			defaultProps: {
				variant: 'outlined',
			},
		},
		MuiPaper: {
			defaultProps: {
				elevation: 0,
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					width: '100%',
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					'&:hover': {
						backgroundColor: compassPalette.surface.neutral.tableHover,
						cursor: 'pointer',
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottom: '0.5px solid',
					borderBottomColor: 'var(--cx-color-grey10)',
				},
				head: {
					'fontSize': 14,
					'fontStyle': 'normal',
					'fontWeight': typescale.weight.semiBold,
					'lineHeight': '24px',
					'letterSpacing': 0.18,
					'backgroundColor': 'var(--cx-color-white)',
					'borderBottom': '0.5px solid',
					'borderBottomColor': 'var(--cx-color-grey10)',
					'textAlign': 'left',
					'textTransform': 'capitalize',
					'padding': '10px',
					'&:first-of-type': {
						'paddingLeft': '10px',
						'@media (max-width: 400px)': {
							paddingLeft: '8px',
						},
					},
					'&:last-of-type': {
						'paddingRight': '16px',
						'@media (max-width: 400px)': {
							paddingRight: '10px',
						},
					},
				},
				body: {
					'fontSize': 14,
					'lineHeight': 1.5,
					'fontWeight': typescale.weight.regular,
					'letterSpacing': '0.17px',
					'textAlign': 'left',
					'padding': '10px',
					'&:first-of-type': {
						'paddingLeft': '10px',
						'@media (max-width: 400px)': {
							paddingLeft: '10px',
						},
					},
					'&:last-of-type': {
						'paddingRight': '16px',
						'@media (max-width: 400px)': {
							paddingRight: '10px',
						},
					},
				},
			},
		},
		MuiToolbar: {
			styleOverrides: {
				root: {
					minHeight: Sizes.TOP_NAV_HEIGHT,
				},
			},
		},
		MuiTableSortLabel: {
			styleOverrides: {
				root: {
					'&:hover': {
						'color': 'var(--cx-color-text)',
						'& .MuiTableSortLabel-icon': {
							backgroundColor: compassPalette.interaction.neutralHoverOpacity,
							borderRadius: '48px',
							opacity: '100',
						},
					},
					'&:active': {
						'& .MuiTableSortLabel-icon': {
							backgroundColor: compassPalette.interaction.neutralPressOpacity,
							borderRadius: '48px',
							opacity: '100',
						},
					},
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						'backgroundColor': compassPalette.interaction.neutralPressOpacity,
						'&:hover': {
							backgroundColor: compassPalette.interaction.neutralHoverOpacity,
						},
					},
					'&:hover': {
						backgroundColor: compassPalette.interaction.neutralHoverOpacity,
					},
				},
			},
		},
		MuiStepper: {
			styleOverrides: {
				root: {
					'& .Mui-completed': {
						'&.MuiStepIcon-root': {
							fontSize: '2em',
							fill: 'success.main',
							borderColor: 'success.main',
							border: '0px solid',
						},
					},
				},
			},
		},
		MuiStepLabel: {
			styleOverrides: {
				root: {
					'.MuiStepLabel-label': {
						color: 'var(--cx-color-text)',
					},
				},
			},
		},
		MuiStepIcon: {
			styleOverrides: {
				root: {
					'fontSize': '2.5em',
					'border': '2px solid',
					'borderRadius': '50%',
					'borderColor': 'var(--cx-color-border)',
					'fill': 'transparent',
					'.MuiStepIcon-text': {
						fill: 'var(--cx-color-text)',
						fontWeight: '600',
					},
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						backgroundColor: 'transparent',
					},
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						opacity: '1',
					},
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					'backgroundColor': 'var(--cx-color-white)',
					'minHeight': 'unset',
					'&.core-ui-tabs ': {
						'.MuiTab-root': {
							'fontSize': typescale.small.fontSize,
							'&:hover': {
								'backgroundColor': compassPalette.interaction.neutralHoverOpacity,
								'@media (hover: none)': {
									backgroundColor: 'transparent',
								},
							},
							'&:active': {
								backgroundColor: compassPalette.interaction.brandPressOpacity,
							},
							'&:focus-visible': {
								outline: '2px solid var(--cx-color-black)',
								outlineOffset: '-2px',
							},
							'.Mui-selected': {
								color: 'var(--cx-color-interactive)',
							},
							'.Mui-disabled': {
								color: 'var(--cx-color-textDisabled)',
							},
							'.MuiTabs-fixed:has(.Mui-disabled.Mui-selected) .MuiTabs-indicator > span': {
								backgroundColor: 'var(--cx-color-textDisabled)',
							},
							'@media (max-width: 600px)': {
								fontSize: typescale.base.fontSize,
							},
							'@media (max-width: 450px)': {
								minWidth: 'min-content',
								textAlign: 'left',
							},
						},
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: '400',
					fontSize: '14px',
					lineHeight: '20px',
					minWidth: 'fit-content',
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					'&:before': {
						border: 'none',
					},
					'&:hover:not(.Mui-disabled, .Mui-error):before': {
						border: 'none',
					},
					'&:focus .MuiSelect-nativeInput': {
						border: 'none',
						backgroundColor: 'transparent',
					},
					'& .MuiSelect-icon': {
						fontSize: 20,
						color: 'var(--cx-color-grey90)',
					},
					'& .MuiInputBase-input': {
						'padding': '0 !important',
						'&:focus': {
							backgroundColor: 'transparent',
							borderRadius: '4px',
						},
						'@media (max-width: 400px)': {
							whiteSpace: 'unset !important',
							overflow: 'unset !important',
						},
					},
					'&:after': {
						border: 'none',
					},
				},
			},
			variants: [
				{
					props: { color: 'error' },
					style: {
						'&.core-ui-input': {
							'& .MuiOutlinedInput-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--cx-color-critical)',
								},
							},
						},
					},
				},
			],
		},
		MuiSwitch: {
			styleOverrides: {
				root: {
					'width': 36,
					'height': 20,
					'padding': 0,
					'& .MuiSwitch-switchBase': {
						'padding': 0,
						'margin': 2,
						'transitionDuration': '100ms',
						'&.Mui-checked': {
							'transform': 'translate(15px, -0.2px)',
							'color': 'var(--cx-color-white)',
							'& + .MuiSwitch-track': {
								backgroundColor: 'primary',
								opacity: 1,
								border: 0,
							},
							'&.Mui-disabled .MuiSwitch-thumb': {
								backgroundColor: 'var(--cx-color-textSubtle)',
							},
							'&.Mui-disabled': {
								transform: 'translate(15px, -0.2px)',
							},
						},
						'&.Mui-disabled .MuiSwitch-thumb': {
							backgroundColor: 'var(--cx-color-textDisabled)',
						},
						'&.Mui-disabled + .MuiSwitch-track': {
							backgroundColor: compassPalette.interaction.disabledOpacity,
							opacity: 1,
						},
						'&.Mui-disabled': {
							transform: 'translate(1px, -0.8px)',
						},
					},
					'& .MuiSwitch-thumb': {
						boxSizing: 'border-box',
						width: 16,
						height: 16,
					},
					'& .MuiSwitch-track': {
						borderRadius: '16px',
						backgroundColor: 'var(--cx-color-grey70)',
						opacity: 1,
					},
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					'& .MuiRadioGroup-root': {
						'& .MuiRadio-root': {
							'padding': '0 !important',
							'color': 'var(--cx-color-border)',
							'&.Mui-checked': {
								color: 'var(--cx-color-interactive)',
							},
							'&.Mui-disabled': {
								color: 'var(--cx-color-textDisabled)',
							},
						},
						'& .MuiFormControlLabel-root': {
							'flexDirection': 'row-reverse',
							'justifyContent': 'space-between',
							'margin': 0,
							'&:focus-visible': {
								outline: '2px solid var(--cx-color-black)',
								zIndex: 1,
							},
							'@media (hover: none)': {
								'&:active': {
									backgroundColor: compassPalette.interaction.neutralHoverOpacity,
								},
							},
							'@media (hover: hover)': {
								'&:hover': {
									backgroundColor: compassPalette.interaction.neutralHoverOpacity,
								},
							},
						},
						'& .MuiFormControlLabel-label': {
							'color': 'var(--cx-color-text)',
							'fontSize': typescale.base.fontSize,
							'lineHeight': typescale.base.lineHeight,
							'fontWeight': 500,
							'&.Mui-disabled': {
								color: 'var(--cx-color-textDisabled)',
							},
						},
					},
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: 0,
					color: 'var(--cx-color-border)',
				},
			},
		},
		MuiStack: {
			styleOverrides: {
				root: {
					'&.metric-card-wrapper': {
						'& .MuiCard-root': {
							'padding': 12,
							'paddingBottom': 28,
							'@media (max-width: 900px)': {
								padding: 12,
							},
						},
					},
					'&.bi-listings-header': {
						'& .MuiAlert-icon': {
							marginRight: '12px',
						},
						'& .MuiAlert-message': {
							'marginLeft': 0,
							'marginRight': 0,
							'@media (max-width: 900px)': {
								marginLeft: 0,
								marginRight: 0,
							},
						},
					},
					'&.engagement-header': {
						'& .MuiAlert-icon': {
							marginRight: '12px',
							padding: '18px 12px',
						},
						'& .MuiAlert-message': {
							'marginLeft': 0,
							'marginRight': 0,
							'@media (max-width: 900px)': {
								marginLeft: 0,
								marginRight: 0,
							},
							'& .MuiStack-root': {
								margin: '14px 0',
							},
							'& .MuiBox-root': {
								marginBottom: '8px',
							},
						},
					},
					'&.listing-segmented-controller': {
						'& .MuiToggleButtonGroup-root': {
							'padding': 2,
							'borderRadius': 4,
							'gap': 0,
							'width': 'auto',
							'@media (max-width: 900px)': {
								width: '100%',
							},
							'@media (max-width: 1279px)': {
								width: '100%',
							},
							'& .MuiToggleButtonGroup-grouped': {
								'paddingTop': 8,
								'paddingBottom': 8,
								'@media (max-width: 1279px)': {
									width: '50%',
								},
								'& .MuiTypography-root': {
									fontWeight: 500,
									fontSize: typescale.base.fontSize,
									lineHeight: typescale.base.lineHeight,
								},
							},
						},
					},
				},
			},
		},
	},
})
const compassTheme = responsiveFontSizes(theme)

export default compassTheme
